import React from "react";
import isNil from "lodash/isNil";

import { Box, Divider, Tooltip, Typography } from "@mui/material";

import { PricingLayout, RoutePricing } from "../../types";
import { toPercentString } from "../../utils/currency";
import { renderBaseRateFieldLabel } from "./utils/displayUtils";
import { useOperatorCurrency } from "globals/hooks";
import {
  derivePricingGroups,
  PricingLineItemKey,
} from "./utils/pricingGroups";
import { grayMedium } from "../../design-system/colors";

export type PricingItemsBlockProps = {
  pricing: RoutePricing;
  pricingLayout?: Pick<PricingLayout, "calculatedOnSubtotalItems" | "subtotalOrder" | "calculatedOnSubtotalOrder"> | null;
};

type PricingListItem = {
  key: PricingLineItemKey;
  title: string;
  amount: number | null;
  percent?: number | null;
  isNegative?: boolean;
};

function PricingItemsBlock(props: PricingItemsBlockProps) {
  const { pricing, pricingLayout } = props;
  const currency = useOperatorCurrency();

  const { subtotalItems, calculatedOnSubtotalItems } = derivePricingGroups(
    pricingLayout
  );
  const hasCalcOnSubtotal = calculatedOnSubtotalItems.length > 0;

  // Build a map from line item key to its pricing data
  const lineItemMap: Record<PricingLineItemKey, PricingListItem> = {
    driverGratuity: {
      key: "driverGratuity",
      title: "Driver Gratuity",
      amount: pricing.driverGratuityAmt,
      percent: pricing.driverGratuityPercent,
    },
    promoDiscount: {
      key: "promoDiscount",
      title: "Discount",
      amount: pricing.promoDiscountAmt,
      percent: pricing.promoDiscountPercent,
      isNegative: true,
    },
    tax: {
      key: "tax",
      title: "Tax",
      amount: pricing.taxAmt,
      percent: pricing.taxPercent,
    },
    tolls: {
      key: "tolls",
      title: "Tolls",
      amount: pricing.tollsAmt,
    },
    meetGreet: {
      key: "meetGreet",
      title: "Meet & Greet",
      amount: pricing.meetGreetAmt,
    },
    other: {
      key: "other",
      title: pricing.otherName || "Other",
      amount: pricing.otherAmt,
      percent: pricing.otherPercent,
    },
    other2: {
      key: "other2",
      title: pricing.other2Name || "Other 2",
      amount: pricing.other2Amt,
      percent: pricing.other2Percent,
    },
    other3: {
      key: "other3",
      title: pricing.other3Name || "Other 3",
      amount: pricing.other3Amt,
      percent: pricing.other3Percent,
    },
    other4: {
      key: "other4",
      title: pricing.other4Name || "Other 4",
      amount: pricing.other4Amt,
      percent: pricing.other4Percent,
    },
    other5: {
      key: "other5",
      title: pricing.other5Name || "Other 5",
      amount: pricing.other5Amt,
      percent: pricing.other5Percent,
    },
    other6: {
      key: "other6",
      title: pricing.other6Name || "Other 6",
      amount: pricing.other6Amt,
      percent: pricing.other6Percent,
    },
    stc: {
      key: "stc",
      title: "Standard Transportation Costs",
      amount: pricing.stcAmt,
      percent: pricing.stcPercent,
    },
    creditCardServiceFee: {
      key: "creditCardServiceFee",
      title: "Credit Card Service Fee",
      amount: pricing.creditCardServiceFeeAmt,
      percent: pricing.creditCardServiceFeePercent,
    },
  };

  const renderLineItem = (item: PricingListItem, baseAmt: number) => {
    const { amount, percent, title, isNegative } = item;

    if (isNil(amount) && isNil(percent)) return null;

    const displayTitle = !isNil(percent)
      ? `${title} (${toPercentString(percent)})`
      : title;

    const displayAmount = !isNil(amount)
      ? amount
      : percent * baseAmt;

    return (
      <PricingItem
        key={title}
        title={displayTitle}
        isNegative={isNegative}
        amount={displayAmount}
      />
    );
  };

  // Non-configurable items (child seats, promo code)
  const childSeatItems = [
    {
      title:
        pricing.rearFacingSeatQuantity > 1
          ? `${pricing.rearFacingSeatQuantity} X Rear-Facing Seat`
          : "Rear-Facing Seat",
      amount: pricing.rearFacingSeatAmt,
    },
    {
      title:
        pricing.forwardFacingSeatQuantity > 1
          ? `${pricing.forwardFacingSeatQuantity} X Forward-Facing Seat`
          : "Forward-Facing Seat",
      amount: pricing.forwardFacingSeatAmt,
    },
    {
      title:
        pricing.boosterSeatQuantity > 1
          ? `${pricing.boosterSeatQuantity} X Booster Seat`
          : "Booster Seat",
      amount: pricing.boosterSeatAmt,
    },
  ];

  const promoCodeItem = {
    title: `Promo Code (${pricing.promoCodeName})`,
    amount: pricing.promoCodeAmt,
    percent: pricing.promoCodePercent,
    isNegative: true,
  };

  if (!!pricing.cancellationFee) {
    return (
      <Box display="flex" flexDirection="column">
        <PricingItem
          title="Cancellation Fee"
          amount={pricing.cancellationFee}
        />
      </Box>
    );
  }

  // Compute subtotal for display
  const computeSubtotal = (): number => {
    let subtotal = pricing.baseRateAmt || 0;
    for (const key of subtotalItems) {
      const item = lineItemMap[key];
      if (isNil(item.amount) && isNil(item.percent)) continue;
      const amt = !isNil(item.amount)
        ? item.amount
        : item.percent * pricing.baseRateAmt;
      if (item.isNegative) {
        subtotal -= amt || 0;
      } else {
        subtotal += amt || 0;
      }
    }
    // Include child seats and promo code in subtotal
    for (const cs of childSeatItems) {
      if (!isNil(cs.amount)) subtotal += cs.amount;
    }
    if (!isNil(promoCodeItem.amount)) {
      subtotal -= promoCodeItem.amount;
    } else if (!isNil(promoCodeItem.percent)) {
      subtotal -= promoCodeItem.percent * pricing.baseRateAmt;
    }
    return subtotal;
  };

  const subtotalAmtForDisplay = computeSubtotal();

  return (
    <Box display="flex" flexDirection="column">
      {/* Base Rate */}
      {!isNil(pricing.baseRateAmt) && (
        <PricingItem
          title={renderBaseRateFieldLabel(
            pricing.hourlyBaseRate,
            pricing.hourlyBaseRateHours,
            currency
          )}
          amount={pricing.baseRateAmt}
        />
      )}

      {/* Subtotal group items */}
      {subtotalItems.map((key) => renderLineItem(lineItemMap[key], pricing.baseRateAmt))}

      {/* Child seats (always in subtotal group) */}
      {childSeatItems.map((item) => {
        if (isNil(item.amount)) return null;
        return (
          <PricingItem key={item.title} title={item.title} amount={item.amount} />
        );
      })}

      {/* Promo Code (always in subtotal group) */}
      {(!isNil(promoCodeItem.amount) || !isNil(promoCodeItem.percent)) && (
        <PricingItem
          title={promoCodeItem.title}
          isNegative
          amount={
            !isNil(promoCodeItem.amount)
              ? promoCodeItem.amount
              : promoCodeItem.percent * pricing.baseRateAmt
          }
        />
      )}

      {/* Subtotal divider + calculated-on-subtotal items */}
      {hasCalcOnSubtotal && (
        <>
          <Box my={1}>
            <Divider />
          </Box>
          <Box display="flex" flex="1" flexDirection="row" mb={2}>
            <Box display="flex" flex="1.5">
              <Typography variant="h6" sx={{ fontWeight: "bold", color: grayMedium }}>
                Subtotal
              </Typography>
            </Box>
            <Box display="flex" flex=".5" justifyContent="flex-end">
              <Typography variant="h6" sx={{ fontWeight: "bold", color: grayMedium }}>
                {currency(subtotalAmtForDisplay)}
              </Typography>
            </Box>
          </Box>
          {calculatedOnSubtotalItems.map((key) =>
            renderLineItem(lineItemMap[key], subtotalAmtForDisplay)
          )}
        </>
      )}
    </Box>
  );
}

export default PricingItemsBlock;

type PricingItemType = {
  title: string;
  amount: number;
  isNegative?: boolean;
};

function PricingItem(props: PricingItemType) {
  const { amount, isNegative, title } = props;
  const currency = useOperatorCurrency();
  return (
    <Box display="flex" flex="1" flexDirection="row" mb={2}>
      <Box display="flex" flex="1.5" minWidth={10}>
        <Tooltip title={title} style={{ cursor: "pointer" }}>
          <Typography variant="h6" noWrap>
            {title}
          </Typography>
        </Tooltip>
      </Box>
      <Box display="flex" flex=".5" justifyContent="flex-end">
        <Typography variant="h6">{`${isNegative ? "-" : ""}${currency(
          amount
        )}`}</Typography>
      </Box>
    </Box>
  );
}
